/**
 * FinanceStudentAccounts — FM1 Staff Finance Student Accounts Workspace
 *
 * Route: /finance/accounts
 * RBAC: finance.account.view.any
 *       (registrar, finance_officer, finance_manager, institution_admin, super_admin)
 *
 * Provides:
 *   - Student search / selector (uses existing canonical students API)
 *   - Per-student finance summary (GET /api/core/students/{id}/finance)
 *   - Charges list (GET /api/core/students/{id}/finance/charges)
 *   - Payments list (GET /api/core/students/{id}/finance/payments)
 *   - A01 Statement download (GET /api/core/students/{id}/finance/statement)
 *
 * FM1 SCOPE: Read-only canonical view. No write operations.
 * FM0-D1 §3.1: "Fees & Student Accounts" — second Finance nav item.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Search, User, Download, ChevronDown, ChevronUp,
  Loader2, AlertCircle, FileText
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MoneyAmount, MoneyBadge } from "../components/finance/MoneyAmount";
import { FinanceCapabilityState } from "../components/finance/FinanceCapabilityState";
import { PageHeader } from "../components/layout/Shell";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Input } from "../components/ui/input";

const STAFF_FINANCE_ROLES = new Set([
  "registrar", "finance_officer", "finance_manager", "cfo",
  "institution_admin", "super_admin", "programme_manager",
]);

const STATUS_COLORS = {
  FINALIZED: "bg-slate-700 text-slate-200",
  CHARGED: "bg-blue-900/50 text-blue-300",
  PAID: "bg-emerald-900/50 text-emerald-300",
  PARTIALLY_PAID: "bg-amber-900/50 text-amber-300",
  OUTSTANDING: "bg-red-900/40 text-red-300",
  UNAPPLIED: "bg-purple-900/40 text-purple-300",
  POSTED: "bg-emerald-900/50 text-emerald-300",
  CANCELLED: "bg-slate-800 text-slate-500",
  CREDITED: "bg-cyan-900/40 text-cyan-300",
  REFUNDED: "bg-orange-900/40 text-orange-300",
};

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-slate-800 text-slate-400";
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  );
}

export default function FinanceStudentAccounts() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [charges, setCharges] = useState(null);
  const [payments, setPayments] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");

  const canView = STAFF_FINANCE_ROLES.has(user?.role);

  // Load student list
  useEffect(() => {
    if (!canView) return;
    setStudentsLoading(true);
    api
      .get("/core/students")
      .then((r) => setStudents(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setStudents([]))
      .finally(() => setStudentsLoading(false));
  }, [canView]);

  // Load finance when student selected
  const loadStudentFinance = useCallback(async (student) => {
    setSelectedStudent(student);
    setFinanceData(null);
    setCharges(null);
    setPayments(null);
    setFinanceError(null);
    setActiveTab("summary");
    setFinanceLoading(true);
    try {
      const [summaryRes, chargesRes, paymentsRes] = await Promise.all([
        api.get(`/core/students/${student.id}/finance`),
        api.get(`/core/students/${student.id}/finance/charges`),
        api.get(`/core/students/${student.id}/finance/payments`),
      ]);
      setFinanceData(summaryRes.data);
      setCharges(chargesRes.data);
      setPayments(paymentsRes.data);
    } catch (e) {
      setFinanceError(
        e?.response?.data?.detail || "Failed to load finance data."
      );
    } finally {
      setFinanceLoading(false);
    }
  }, []);

  const handleDownloadStatement = async () => {
    if (!selectedStudent) return;
    try {
      const resp = await api.get(
        `/core/students/${selectedStudent.id}/finance/statement?format=pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `fee_statement_${selectedStudent.student_number || selectedStudent.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "A01_PDF_RENDERER_ENVIRONMENT_BLOCKED") {
        alert("PDF renderer is not available in this environment. JSON statement is available via the API.");
      } else {
        alert("Failed to download statement.");
      }
    }
  };

  const filteredStudents = students.filter((s) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (s.display_name || "").toLowerCase().includes(q) ||
      (s.student_number || "").toLowerCase().includes(q) ||
      (s.given_name || "").toLowerCase().includes(q) ||
      (s.family_name || "").toLowerCase().includes(q)
    );
  });

  if (!canView) {
    return (
      <div className="space-y-6">
        <PageHeader title="Fees & Student Accounts" />
        <FinanceCapabilityState
          state="NOT_AVAILABLE"
          title="Access Restricted"
          reason="finance.account.view.any permission required. Contact your Finance administrator."
          data-testid="finance-accounts-no-access"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-student-accounts-page">
      <PageHeader
        title="Fees & Student Accounts"
        description="Canonical read — charges, payments, and account balance per student."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Student Selector */}
        <div
          className="lg:col-span-1 space-y-3"
          data-testid="finance-student-selector"
        >
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <Input
              data-testid="finance-student-search"
              placeholder="Search students…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-900 border-slate-700 text-slate-200 placeholder-slate-500 text-sm"
            />
          </div>

          <div className="max-h-[60vh] overflow-y-auto space-y-1 rounded-lg border border-slate-800 bg-slate-950 p-1">
            {studentsLoading && (
              <div className="flex items-center justify-center p-6 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            )}
            {!studentsLoading && filteredStudents.length === 0 && (
              <p className="text-center text-xs text-slate-600 py-6">No students found.</p>
            )}
            {filteredStudents.map((s) => (
              <button
                key={s.id}
                data-testid={`finance-student-item-${s.id}`}
                onClick={() => loadStudentFinance(s)}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors text-sm ${
                  selectedStudent?.id === s.id
                    ? "bg-blue-900/40 border border-blue-700/50 text-slate-100"
                    : "hover:bg-slate-800 text-slate-300"
                }`}
              >
                <p className="font-medium text-xs">
                  {s.display_name ||
                    [s.given_name, s.family_name].filter(Boolean).join(" ") ||
                    "Name pending"}
                </p>
                <p className="text-[10px] text-slate-500">
                  {s.student_number || s.id?.slice(0, 8)}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Finance Panel */}
        <div className="lg:col-span-2 space-y-4" data-testid="finance-account-panel">
          {!selectedStudent && !financeLoading && (
            <div className="flex flex-col items-center justify-center h-48 text-center rounded-lg border border-slate-800 bg-slate-950">
              <User className="h-8 w-8 text-slate-700 mb-2" />
              <p className="text-sm text-slate-500">Select a student to view their Finance account.</p>
            </div>
          )}

          {financeLoading && (
            <div className="flex items-center justify-center h-48 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading finance data…
            </div>
          )}

          {financeError && (
            <div
              data-testid="finance-account-error"
              className="flex items-center gap-2 p-4 rounded-lg bg-red-950/30 border border-red-900/40 text-red-300 text-sm"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              {typeof financeError === "string" ? financeError : JSON.stringify(financeError)}
            </div>
          )}

          {selectedStudent && financeData && !financeLoading && (
            <>
              {/* Student Header */}
              <div
                className="flex items-center justify-between p-4 rounded-lg bg-slate-900 border border-slate-800"
                data-testid="finance-account-header"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-100">
                    {selectedStudent.display_name ||
                      [selectedStudent.given_name, selectedStudent.family_name].filter(Boolean).join(" ") ||
                      "Name pending"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {financeData.student_number || selectedStudent.student_number || selectedStudent.id?.slice(0, 8)}
                    {" · "}
                    <span className="text-slate-600">{financeData.account_status}</span>
                  </p>
                </div>
                <button
                  data-testid="finance-download-statement"
                  onClick={handleDownloadStatement}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-900/40 hover:bg-blue-800/50 border border-blue-700/40 text-blue-300 text-xs font-medium transition-colors"
                  title="Download A01 Fee Statement PDF"
                >
                  <Download className="h-3.5 w-3.5" />
                  Statement (A01)
                </button>
              </div>

              {/* Summary Cards */}
              <div
                className="grid grid-cols-2 sm:grid-cols-4 gap-3"
                data-testid="finance-summary-cards"
              >
                <SummaryCard
                  label="Outstanding"
                  amount={financeData.outstanding_balance}
                  currency={financeData.currency}
                  variant="outstanding"
                  testId="finance-card-outstanding"
                />
                <SummaryCard
                  label="Total Charges"
                  amount={financeData.total_finalized_charges}
                  currency={financeData.currency}
                  variant="neutral"
                  testId="finance-card-charges"
                />
                <SummaryCard
                  label="Total Paid"
                  amount={financeData.total_posted_payments}
                  currency={financeData.currency}
                  variant="paid"
                  testId="finance-card-payments"
                />
                <SummaryCard
                  label="Unapplied Cash"
                  amount={financeData.unapplied_cash}
                  currency={financeData.currency}
                  variant="unapplied"
                  testId="finance-card-unapplied"
                />
              </div>

              {/* Tabs */}
              <div className="border-b border-slate-800 flex gap-1">
                {["summary", "charges", "payments"].map((tab) => (
                  <button
                    key={tab}
                    data-testid={`finance-tab-${tab}`}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-xs font-medium capitalize transition-colors border-b-2 -mb-px ${
                      activeTab === tab
                        ? "border-blue-500 text-blue-400"
                        : "border-transparent text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {tab}
                    {tab === "charges" && charges && (
                      <span className="ml-1 text-slate-600">({charges.count})</span>
                    )}
                    {tab === "payments" && payments && (
                      <span className="ml-1 text-slate-600">({payments.count})</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === "summary" && (
                <SummaryTab data={financeData} />
              )}
              {activeTab === "charges" && (
                <ChargesTab charges={charges?.items || []} currency={financeData.currency} />
              )}
              {activeTab === "payments" && (
                <PaymentsTab payments={payments?.items || []} currency={financeData.currency} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, amount, currency, variant, testId }) {
  const variantClasses = {
    outstanding: "border-red-900/40 bg-red-950/20",
    paid: "border-emerald-900/40 bg-emerald-950/20",
    unapplied: "border-blue-900/40 bg-blue-950/20",
    neutral: "border-slate-800 bg-slate-900",
  };
  return (
    <div
      data-testid={testId}
      className={`p-3 rounded-lg border space-y-1 ${variantClasses[variant] || variantClasses.neutral}`}
    >
      <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{label}</p>
      <MoneyAmount amount={amount} currency={currency} size="base" />
    </div>
  );
}

function SummaryTab({ data }) {
  const rows = [
    ["Account ID", data.account_id?.slice(0, 16) + "…"],
    ["Account Status", data.account_status],
    ["Currency", data.currency],
    ["Charge Count", data.charge_count],
    ["Payment Count", data.payment_count],
    ["Total Credits Applied", <MoneyBadge key="credits" amount={data.total_applied_credits} currency={data.currency} variant="paid" />],
    ["Total Refunds", <MoneyBadge key="refunds" amount={data.total_posted_refunds} currency={data.currency} />],
    ["As of", data.as_of ? data.as_of.replace("T", " ").slice(0, 19) + " UTC" : "—"],
  ];
  return (
    <table className="w-full text-xs" data-testid="finance-summary-table">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label} className="border-b border-slate-800/50">
            <td className="py-2 pr-4 text-slate-500 font-medium w-40">{label}</td>
            <td className="py-2 text-slate-200">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ChargesTab({ charges, currency }) {
  if (charges.length === 0) {
    return (
      <FinanceCapabilityState
        state="NOT_AVAILABLE"
        title="No Charges"
        reason="No finalized charges exist for this student account."
        data-testid="finance-charges-empty"
      />
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="finance-charges-table">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800">
            <TableHead className="text-slate-500 text-xs">Date</TableHead>
            <TableHead className="text-slate-500 text-xs">Description</TableHead>
            <TableHead className="text-slate-500 text-xs">Status</TableHead>
            <TableHead className="text-slate-500 text-xs text-right">Amount</TableHead>
            <TableHead className="text-slate-500 text-xs text-right">Open</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {charges.map((c) => (
            <TableRow key={c.id} className="border-slate-800/50">
              <TableCell className="text-xs text-slate-400">{(c.charge_date || "—").slice(0, 10)}</TableCell>
              <TableCell className="text-xs text-slate-300 max-w-[180px] truncate">{c.description || "Fee charge"}</TableCell>
              <TableCell><StatusBadge status={c.status} /></TableCell>
              <TableCell className="text-right">
                <MoneyAmount amount={c.original_amount} currency={currency} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <MoneyAmount amount={c.open_amount} currency={currency} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PaymentsTab({ payments, currency }) {
  if (payments.length === 0) {
    return (
      <FinanceCapabilityState
        state="NOT_AVAILABLE"
        title="No Payments"
        reason="No posted payments exist for this student account."
        data-testid="finance-payments-empty"
      />
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="finance-payments-table">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800">
            <TableHead className="text-slate-500 text-xs">Date</TableHead>
            <TableHead className="text-slate-500 text-xs">Method</TableHead>
            <TableHead className="text-slate-500 text-xs">Status</TableHead>
            <TableHead className="text-slate-500 text-xs text-right">Amount</TableHead>
            <TableHead className="text-slate-500 text-xs text-right">Unapplied</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((p) => (
            <TableRow key={p.id} className="border-slate-800/50">
              <TableCell className="text-xs text-slate-400">{(p.payment_date || "—").slice(0, 10)}</TableCell>
              <TableCell className="text-xs text-slate-300">{p.payment_method}</TableCell>
              <TableCell><StatusBadge status={p.status} /></TableCell>
              <TableCell className="text-right">
                <MoneyAmount amount={p.payment_amount} currency={currency} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <MoneyAmount amount={p.unapplied_amount} currency={currency} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
